"use client";

import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const perks = [{
  numeral: "i.",
  text: "Early access to limited-edition awareness merchandise drops"
}, {
  numeral: "ii.",
  text: "Invitations to community advocacy workshops and local events"
}, {
  numeral: "iii.",
  text: "Direct impact reports on how your support funds humanitarian causes"
}];
function CircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-foreground/20 pt-10">
        <p className="font-default text-2xl text-foreground italic mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-foreground/30 text-foreground hover:bg-foreground/10">
          Register another supporter
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="email" render={({
      field
    }) => <FormItem className="group">
            <FormControl>
              <Input type="email" placeholder="your.email@domain.com" {...field} className="h-14 border-0 border-b border-foreground/30 bg-transparent px-0 font-default italic text-lg text-foreground placeholder:text-foreground/40 placeholder:italic focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="text" placeholder="your full name" {...field} className="h-12 border-0 border-b border-foreground/30 bg-transparent px-0 font-default italic text-base text-foreground placeholder:text-foreground/40 placeholder:italic focus-visible:ring-0 focus-visible:border-primary transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="zip_code" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input type="text" placeholder="postal code" {...field} className="h-12 border-0 border-b border-foreground/30 bg-transparent px-0 font-default italic text-base text-foreground placeholder:text-foreground/40 placeholder:italic focus-visible:ring-0 focus-visible:border-primary transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="cause_interest" render={({
        field
      }) => <FormItem>
              <FormControl>
                <div className="relative">
                  <select {...field} className="h-12 w-full appearance-none border-0 border-b border-foreground/30 bg-transparent px-0 pr-8 font-default italic text-base text-foreground focus:outline-none focus:border-primary transition-colors">
                    <option value="" className="bg-background text-foreground">primary cause interest</option>
                    <option value="Food Security" className="bg-background text-foreground">Food Security</option>
                    <option value="Clean Water" className="bg-background text-foreground">Clean Water</option>
                    <option value="Education" className="bg-background text-foreground">Education</option>
                    <option value="Other" className="bg-background text-foreground">Other</option>
                  </select>
                  <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 size-4 text-foreground/50 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem>
            <FormControl>
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-1 shrink-0">
                  <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="peer sr-only" />
                  <div className="size-4 border border-foreground/40 rounded-md peer-checked:bg-primary peer-checked:border-primary transition-colors flex items-center justify-center">
                    {field.value && <Check className="size-3 text-primary-foreground" />}
                  </div>
                </div>
                <span className="font-default text-xs text-foreground/70 leading-relaxed">
                  I agree to receive mission updates and exclusive impact reports.
                </span>
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/30 p-4 rounded-md">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="group h-14 px-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default text-base shadow-md">
          <span>{isSubmitting ? "Processing..." : "Join the Movement"}</span>
          <ArrowRight className="size-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>

        <p className="mt-6 font-default text-[11px] text-foreground/50 italic">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </div>
    </Form>;
}
export default function JoinTheMovement() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5291" id="join-the-movement" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="pointer-events-none absolute top-0 left-0 w-[60%] h-[60%] opacity-30" style={{
      background: "radial-gradient(circle at 20% 20%, hsl(150 25% 45% / 0.25), transparent 60%)"
    }} aria-hidden="true" />

      <svg className="pointer-events-none absolute -bottom-12 -right-12 md:-bottom-16 md:-right-8 w-64 h-64 md:w-96 md:h-96 opacity-[0.12] text-primary" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="0.6" aria-hidden="true">
        <ellipse cx="100" cy="60" rx="22" ry="14" />
        <ellipse cx="100" cy="78" rx="28" ry="16" />
        <ellipse cx="100" cy="96" rx="32" ry="18" />
        <ellipse cx="100" cy="115" rx="34" ry="18" />
        <ellipse cx="100" cy="134" rx="32" ry="17" />
        <ellipse cx="100" cy="152" rx="26" ry="14" />
        <ellipse cx="100" cy="168" rx="18" ry="10" />
        <path d="M100 30 Q 100 50 100 60" />
        <path d="M100 30 Q 92 24 88 18" />
        <path d="M100 30 Q 108 24 112 18" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-24">
          <div ref={leftRef} className={`lg:col-span-6 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-[11px] uppercase tracking-[0.2em] text-primary mb-8">
              Our Mission
            </p>

            <h2 className="font-default font-light text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-foreground leading-[0.95] mb-10">
              Keep awareness{" "}
              <span className="italic text-primary">fresh.</span>
            </h2>

            <p className="font-default text-base md:text-lg text-foreground/80 leading-relaxed max-w-md mb-12">Join a movement built around awareness, compassion, and action — helping keep important issues fresh in people’s minds.
          </p>

            <ul className="space-y-5 max-w-md">
              {perks.map((perk, i) => <li data-index={i} key={i} className="flex gap-4 items-baseline">
                  <span className="font-default italic text-primary text-base shrink-0 w-6">
                    {perk.numeral}
                  </span>
                  <span className="font-default italic text-foreground/85 text-lg leading-relaxed">
                    {perk.text}
                  </span>
                </li>)}
            </ul>
          </div>

          <div ref={rightRef} className={`lg:col-span-6 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:pt-16">
              <FormProvider formKey="join_movement_form_home" formId="join_movement_form_home" sectionName="join-the-movement" defaultValues={{
              name: "",
              email: "",
              zip_code: "",
              cause_interest: "",
              consent: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              zip_code: {
                pattern: "^\\d{5}$"
              },
              consent: {
                required: true
              }
            }} submitText="Join the Movement" successMessage="Thank you for joining our mission. Together, we make a difference." errorMessage="Something went wrong. Please try again.">
                <CircleForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}